import React, { useCallback, useEffect } from 'react';
import { CardViewContentBlockProps } from '../types';
import { Box, Typography } from '@mui/material';
import LibraryCategoryCard from '../../../common/library-category-card/LibraryCategoryCard';
import TerminationIcon from '../../../../assets/svg/termination.svg?react';
import PolicyIcon from '../../../../assets/svg/policy.svg?react';
import ProposalIcon from '../../../../assets/svg/proposal.svg?react';
import ProjectMaterialsIcon from '../../../../assets/svg/project-materials.svg?react';
import InvoicingIcon from '../../../../assets/svg/invoicing.svg?react';
import ReceiptIcon from '../../../../assets/svg/receipt-icon.svg?react';
import { useTranslation } from 'react-i18next';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import DocumentListVault from '../../document-vault/components/document-list-vault/DocumentListVault';
import styles from '../SupplierProfile.module.scss';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { RootState } from '../../../store';
import { ProgressOverlay } from '../../../common/progress-overlay/ProgressOverlay';
import { useDocumentsMetaData } from '../../document-vault/hooks/useDocumentsWorkflowsMetadata';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import OperationsIcon from '../../../../assets/svg/business-to-consumer-plan.svg?react';
import AccountingIcon from '../../../../assets/svg/view-list.svg?react';
import { clearDocuments } from '../../../slices/documents/documentsSlice';

export const SuppliersCategoryContent = ({
  documents,
  workflow,
  setWorkflow,
  showWorkflowBreadcrumbs,
  setShowWorkflowBreadcrumbs,
}: CardViewContentBlockProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const location = useLocation();
  const profileId = location.pathname.split('/supplier-profiles/')[1]?.split('/')[0];
  const navigate = useNavigate();
  const workflowItem = searchParams.get('workflow');
  const dispatch = useAppDispatch();
  const documentUploadsLoading = useAppSelector((state: RootState) => state.documents.status);
  const { uploadedDocumentsMetaData } = useDocumentsMetaData('All', 'suppliers', Number(profileId));

  const getDocsFilteredByEmptyWorkflowCount = () => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === '');
    return metaData ? metaData.count : 0;
  };

  const getDocsCount = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData
      ? metaData.count +
          (workflow === t('labels.generalAdministration')
            ? getDocsFilteredByEmptyWorkflowCount()
            : 0)
      : 0;
  };

  const hasNewDocuments = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData ? metaData.isNew : false;
  };

  const getIcon = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.supplier.agreement'):
        return <PolicyIcon />;
      case t('tagsLabels.documentWorkflow.supplier.proposalOrQuote'):
        return <ProposalIcon />;
      case t('tagsLabels.documentWorkflow.supplier.projectMaterials'):
        return <ProjectMaterialsIcon />;
      case t('tagsLabels.documentWorkflow.supplier.invoicing'):
        return <InvoicingIcon />;
      case t('tagsLabels.documentWorkflow.supplier.receipts'):
        return <ReceiptIcon />;
      case t('tagsLabels.documentWorkflow.supplier.accounting'):
        return <AccountingIcon />;
      case t('tagsLabels.documentWorkflow.supplier.termination'):
        return <TerminationIcon />;
      case t('tagsLabels.documentWorkflow.supplier.generalAdministration'):
        return <OperationsIcon />;
    }
  };

  const getWorkflowNumber = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.supplier.proposalOrQuote'):
        return '1. ';
      case t('tagsLabels.documentWorkflow.supplier.agreement'):
        return '2. ';
      case t('tagsLabels.documentWorkflow.supplier.invoicing'):
        return '3. ';
      case t('tagsLabels.documentWorkflow.supplier.receipts'):
        return '4. ';
      case t('tagsLabels.documentWorkflow.supplier.projectMaterials'):
        return '5. ';
      case t('tagsLabels.documentWorkflow.supplier.accounting'):
        return '6. ';
      case t('tagsLabels.documentWorkflow.supplier.termination'):
        return '7. ';
      case t('tagsLabels.documentWorkflow.supplier.generalAdministration'):
        return '8. ';
    }
  };

  const setWorkflowData = useCallback(() => {
    if (workflowItem) {
      setWorkflow(decodeURI(workflowItem));
      setShowWorkflowBreadcrumbs(true);
      navigate(location.pathname);
    }
  }, [workflowItem, navigate, location.pathname, setWorkflow, setShowWorkflowBreadcrumbs]);

  useEffect(() => {
    setWorkflowData();
  }, [setWorkflowData]);

  return (
    <>
      {!workflow && (
        <Typography
          variant='h3'
          component='h3'
          sx={{ marginBottom: '16px', fontSize: '15px', lineHeight: '24px' }}
        >
          {t('headers.workflows')}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
        }}
      >
        {workflow && showWorkflowBreadcrumbs ? (
          <>
            <Box className={styles.breadcrumbs}>
              <Typography
                variant='h3'
                component='h3'
                className={styles.documentViewText}
                onClick={() => {
                  setWorkflow(null);
                  setShowWorkflowBreadcrumbs(false);
                  dispatch(clearDocuments());
                }}
              >
                {t('headers.workflows')}
              </Typography>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              <Typography variant='body2' className={styles.documentViewText}>
                {workflow === t('headers.general') ? t('labels.generalUnassigned') : workflow}
              </Typography>
            </Box>
            <Box position='relative' width='100%'>
              <DocumentListVault
                documents={[...documents] /*getDocsFilteredByWorkflow(workflow)*/}
              />
              {documentUploadsLoading === 'loading' && (
                <ProgressOverlay className={'position-absolute'} />
              )}
            </Box>
          </>
        ) : (
          <>
            {TAGS_LABELS.documentWorkflow.supplier.map((el) => (
              <LibraryCategoryCard
                title={getWorkflowNumber(t(el)) + t(el)}
                description={
                  getDocsCount(t(el)) +
                  ' document' +
                  (getDocsCount(t(el)) === 0 || getDocsCount(t(el)) > 1 ? 's' : '')
                }
                onClick={() => {
                  setWorkflow(t(el));
                  setShowWorkflowBreadcrumbs(true);
                }}
                icon={getIcon(t(el))}
                key={el}
                hasNewLabel={
                  el === 'tagsLabels.documentWorkflow.supplier.generalAdministration'
                    ? hasNewDocuments(t(el)) || hasNewDocuments('')
                    : hasNewDocuments(t(el))
                }
              />
            ))}
          </>
        )}
      </Box>
    </>
  );
};
